import {
  getMeetingLink,
  isSoloEvent,
  withAuthUser,
  type StartMeetingInput,
} from '@electron/shared/meetings.types';
import { isToday } from 'date-fns/isToday';

import type { UpcomingMeetingWithStatus } from '@/features/meetings/meetings.hooks';

import type { ScheduleFilterMode } from './widgets.types';

export const SCHEDULE_MAX_ROWS = 5;
export const HOME_SCHEDULE_MAX_ROWS = 3;

const startsToday = (meeting: UpcomingMeetingWithStatus): boolean =>
  isToday(new Date(meeting.event.start_datetime));

export const selectScheduleMeetings = (
  events: UpcomingMeetingWithStatus[],
  filterMode: ScheduleFilterMode,
  todayOnly = false,
  maxRows = SCHEDULE_MAX_ROWS,
  excludeSoloEvents = true
) => {
  const inWindow = todayOnly ? events.filter(startsToday) : events;
  const visible =
    filterMode === 'meetings' && excludeSoloEvents
      ? inWindow.filter((m) => !isSoloEvent(m.event))
      : inWindow;
  const meetings = visible.slice(0, maxRows);
  return {
    meetings,
    totalCount: visible.length,
    hasMore: visible.length > meetings.length,
    todayCount: visible.filter(startsToday).length,
    hasAnyEventToday: events.some(startsToday),
  };
};

export type ScheduleMeetingRow = {
  key: string;
  eventId: string;
  meetingId: string | undefined;
  startDate: Date;
  endDateMs: number;
  summary: string;
  hasPrep: boolean;
  joinUrl: string | undefined;
  isMeeting: boolean;
};

export type ScheduleState =
  | { kind: 'loading' }
  | { kind: 'empty-no-calendar' }
  | { kind: 'empty-clear-week' }
  | { kind: 'empty-clear-today' }
  | {
      kind: 'populated';
      rows: ScheduleMeetingRow[];
      totalCount: number;
      hasMore: boolean;
    };

export const isHomeScheduleVisible = ({
  isLoading,
  hasCalendar,
  hasAnyEventToday,
}: {
  isLoading: boolean;
  hasCalendar: boolean;
  hasAnyEventToday: boolean;
}): boolean => isLoading || !hasCalendar || hasAnyEventToday;

export const selectScheduleState = ({
  meetings,
  totalCount,
  hasMore,
  todayOnly,
  hasCalendar,
  isLoading,
}: {
  meetings: UpcomingMeetingWithStatus[];
  totalCount: number;
  hasMore: boolean;
  todayOnly: boolean;
  hasCalendar: boolean;
  isLoading: boolean;
}): ScheduleState => {
  if (isLoading) return { kind: 'loading' };

  if (meetings.length === 0) {
    if (!hasCalendar) return { kind: 'empty-no-calendar' };
    return todayOnly
      ? { kind: 'empty-clear-today' }
      : { kind: 'empty-clear-week' };
  }

  return {
    kind: 'populated',
    rows: meetings.map(({ event, meeting }) => ({
      key: event.id,
      eventId: event.id,
      meetingId: meeting?.id,
      startDate: new Date(event.start_datetime),
      endDateMs: new Date(event.end_datetime).getTime(),
      summary: event.summary,
      hasPrep: Boolean(meeting?.prep),
      joinUrl: withAuthUser(getMeetingLink(event), event),
      isMeeting: !isSoloEvent(event),
    })),
    totalCount,
    hasMore,
  };
};

/** Starts the linked note only when recording is available; the web build still opens the link. */
export const performScheduleJoin = (
  {
    joinUrl,
    eventId,
    title,
    canRecord,
  }: {
    joinUrl: string;
    eventId: string;
    title: string | undefined;
    canRecord: boolean;
  },
  deps: {
    openExternal: (url: string) => void;
    markUpcomingMeetingJoined: (eventId: string) => void;
    startMeetingNote: (input: StartMeetingInput) => void;
  }
): void => {
  deps.openExternal(joinUrl);
  deps.markUpcomingMeetingJoined(eventId);
  if (!canRecord || title === undefined) return;
  deps.startMeetingNote({ startReason: 'calendar_event', eventId, title });
};
